import React from 'react';
import { Box, Drawer, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { PageHint, IDocKey } from './PageHint';

interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  /** Primary-дії у футері (напр. "+ Додати задачу") - те саме правило, що в actions у BaseDialog. */
  actions?: React.ReactNode;
  /** Додаткові контроли в шапці (напр. перемикач фільтра) - між заголовком і хрестиком закриття. */
  headerActions?: React.ReactNode;
  /** Ширина панелі на `sm`+; на мобільних завжди на весь екран. За замовч. 600 (Figma). */
  width?: number | string;
  iDoc?: IDocKey;
  contentSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
}

/**
 * Універсальна бокова панель, що виїжджає справа - аналог `BaseDialog`, але для контенту, якому
 * тісно в модалці (довгі списки, деталізація сутності тощо). Будь-яка нова бокова панель проєкту =
 * `SidePanel`, не власний `Drawer` з нуля - той самий принцип, що й "будь-яка модалка = BaseDialog".
 *
 * Хрестик закриття вбудований у шапку. `actions` (якщо передано) - футер з primary-діями, той самий
 * принцип, що в `BaseDialog`: не дублювати кнопку "Закрити" там.
 */
export const SidePanel: React.FC<SidePanelProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  headerActions,
  width = 600,
  iDoc,
  contentSx,
  paperSx,
  headerSx,
  titleSx,
  actionsSx,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: width }, display: 'flex', flexDirection: 'column', ...paperSx },
        ...(iDoc ? { 'data-i-doc': iDoc } : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
          ...headerSx,
        }}
      >
        <Typography sx={{ fontSize: 20, lineHeight: '23px', fontWeight: 600, color: '#26313F', ...titleSx }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {headerActions}
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {iDoc && <PageHint iDoc={iDoc} />}

      <Box sx={{ flexGrow: 1, overflowY: 'auto', ...contentSx }}>
        {children}
      </Box>

      {actions && (
        <Box
          sx={{
            display: 'flex', justifyContent: 'flex-end', gap: 1,
            px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0,
            ...actionsSx,
          }}
        >
          {actions}
        </Box>
      )}
    </Drawer>
  );
};
